import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import WifiLogo from "../images/wifi-2-logo-png-transparent.png";
import { MdCloudOff, MdPowerSettingsNew } from "react-icons/md";
import {
  FaChevronUp,
  FaNetworkWired,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useLanguage } from "../pages/LanguageContext";

const wifiFAQ = {
  en: [
    {
      q: " Can’t Wi-Fi devices do the same thing as Zigbee or Matter?",
      a: "Not reliably. While they may offer similar features, Wi-Fi devices often suffer from lag, signal issues, and cloud dependency. Zigbee and Matter are designed specifically for smart home automation, offering faster response, local control, and better scalability.",
    },
    {
      q: " Why do so many companies still use Wi-Fi devices?",
      a: "Because they’re cheaper and easier to source. But “easy” doesn’t mean “right.” At Mass Smart Solutions, we focus on long-term reliability and a seamless user experience — not short-term convenience.",
    },
    {
      q: " What if I already have some Wi-Fi smart devices?",
      a: "That’s fine. We can help you gradually upgrade to a more stable system using Zigbee and Matter. In the meantime, we’ll make sure your current devices work as smoothly as possible.",
    },
    {
      q: " Are Zigbee and Matter more expensive?",
      a: "Not necessarily. While high-quality devices using these protocols may have a slightly higher upfront cost, they save money long-term through lower maintenance, energy efficiency, and reduced replacement needs.",
    },
    {
      q: " Is Wi-Fi ever used in your projects?",
      a: "Only in very specific cases where no better alternative exists — and even then, never for core systems. We avoid building full automation on Wi-Fi because it’s simply not reliable for modern smart homes.",
    },
  ],
  ar: [
    {
      q: " هل أجهزة الواي فاي تقوم بنفس وظائف Zigbee أو Matter؟",
      a: "ليس بشكل موثوق. قد تبدو متشابهة في المميزات، لكن أجهزة الواي فاي تعاني من مشاكل في السرعة والثبات والاعتماد الكامل على الإنترنت. أما Zigbee وMatter فمصممة خصيصًا لأتمتة المنازل بفعالية أعلى",
    },
    {
      q: " لماذا لا تزال بعض الشركات تستخدم أجهزة واي فاي؟",
      a: "لأنها أرخص وأسهل في التوريد. لكن في ماس، نركز على الجودة طويلة المدى وتجربة المستخدم الممتازة، وليس على الحلول السريعة",
    },
    {
      q: " وماذا لو كان لدي أجهزة واي فاي في منزلي؟",
      a: "لا مشكلة. يمكننا مساعدتك على الترقية تدريجيًا إلى نظام أكثر استقرارًا باستخدام Zigbee وMatter. وسندمج الأجهزة الموجودة بطريقة لا تؤثر على الأداء العام",
    },
    {
      q: " هل أجهزة Zigbee وMatter أغلى من أجهزة الواي فاي؟",
      a: "ليست دائمًا. قد تكون التكلفة الأولية أعلى قليلًا، لكنها توفر المال على المدى الطويل من خلال استهلاك أقل للطاقة وأداء أكثر استقرارًا",
    },
    {
      q: " هل تستخدمون الواي فاي في مشاريعكم أحيانًا؟",
      a: "فقط في حالات خاصة جدًا لا يتوفر فيها بديل أفضل. ولا نعتمد عليه أبدًا كنظام أساسي في الأتمتة، لأنه لا يقدم الكفاءة المطلوبة لمنازل ذكية حديثة",
    },
  ],
};

const titles = {
  en: {
    main: "Wi-Fi Smart Home Devices",
    subtitle:
      "Familiar, but fundamentally flawed for smart automation Wi-Fi smart devices are everywhere, often chosen for their affordability and quick setup. Many companies still rely on them today.At Mass Smart Solutions, we’ve taken a different path because when it comes to building a stable and scalable smart home, Wi-Fi simply doesn’t measure up",
    faq: "Wi-Fi FAQ",
  },
  ar: {
    main: "أجهزة المنزل الذكي واي فاي",
    subtitle:
      "منتشر ومعروف، لكنه لا يرقى لمستوى الأتمتة الذكية الحقيقيةتنتشر الأجهزة الذكية المعتمدة على الواي فاي بكثرة، وغالبًا ما تُختار لأنها رخيصة وسهلة الإعداد. ولا تزال العديد من الشركات تعتمد عليها حتى اليوم.لكن في ماس  للحلول الذكية، اخترنا طريقًا مختلفًا لأن بناء منزل ذكي مستقر وقابل للتوسع لا يمكن أن يعتمد على الواي فاي وحده",
    faq: "الأسئلة الشائعة عن الواي فاي",
  },
};

const WifiPage = () => {
  const [showButton, setShowButton] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const limitations = [
    {
      icon: <FaExclamationTriangle />,
      en: {
        title: "Unstable with Multiple Devices",
        desc: "Wi-Fi wasn’t built to handle dozens of smart devices at once. As your system grows, performance drops, resulting in delays, disconnects, and user frustration.",
      },
      ar: {
        title: "غير مستقر عند تعدد الأجهزة",
        desc: "شبكة الواي فاي لم تُصمم لتحمّل عشرات الأجهزة الذكية في نفس الوقت. ومع زيادة عدد الأجهزة، تنخفض الكفاءة ويظهر التأخير والانقطاع",
      },
    },
    {
      icon: <MdPowerSettingsNew />,
      en: {
        title: "Drains Power Quickly",
        desc: "Wi-Fi devices consume much more energy than Zigbee or Matter, making them impractical for long-lasting sensors or battery-powered gadgets",
      },
      ar: {
        title: "استهلاك طاقة مرتفع",
        desc: "أجهزة الواي فاي تستهلك طاقة أكثر بكثير من أجهزة Zigbee أو Matter، مما يجعلها غير مناسبة للأجهزة المعتمدة على البطارية أو الحسّاسات طويلة الأمد",
      },
    },
    {
      icon: <FaNetworkWired />,
      en: {
        title: "No Mesh, No Coverage",
        desc: "Unlike Zigbee’s self-healing mesh network, Wi-Fi doesn’t extend itself. The result is dead zones, lag, and inconsistent device behavior.",
      },
      ar: {
        title: "لا توجد شبكة متشابكة",
        desc: "على عكس Zigbee التي تنشئ شبكة مترابطة ذاتيًا، لا يمكن للواي فاي أن يمتد بنفس الطريقة، ما يؤدي إلى وجود مناطق ميتة وتأخير في الاستجابة",
      },
    },
    {
      icon: <MdCloudOff />,
      en: {
        title: "Cloud-Only Limitations",
        desc: "Most Wi-Fi devices depend entirely on the cloud. If your internet goes out, your smart home stops working. That’s not automation — it’s a risk.",
      },
      ar: {
        title: "الاعتماد الكامل على الإنترنت",
        desc: "أغلب الأجهزة المعتمدة على الواي فاي تعمل فقط من خلال الإنترنت. وإذا انقطع الاتصال، يتوقف نظامك بالكامل. هذا ليس ذكاءً حقيقياً، بل نقطة ضعف",
      },
    },
  ];
  return (
    <>
      <style>{`
        body.dark, .dark main.wifi-page { background: #111 !important; color: #fff !important; }
        .dark .wifi-header,
        .dark .wifi-faq-card,
        .dark .wifi-faq-title {
          background: #111 !important;
          color: #fff !important;
        }
        .dark .wifi-header h1,
        .dark .wifi-header h2,
        .dark .wifi-header h3,
        .dark .wifi-faq-title {
          color: #1b4fbdff !important;
        }
        .dark .wifi-logo {
          filter: brightness(0) invert(1);
        }
        .dark .wifi-feature-card {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.5px solid #fff !important;
          box-shadow: 0 10px 32px #2297e62a !important;
        }
        .dark .wifi-feature-card h3 {
          color: #1b4fbdff !important;
        }
        .dark .wifi-feature-card p {
          color: #ffffffff !important;
        }
        .dark .wifi-feature-card .wifi-icon {
          color: #1b4fbdff !important;
        }
        .dark .wifi-faq-card details {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.2px solid #fff !important;
        }
        .dark .wifi-faq-card summary {
          color: #1b4fbdff !important;
          font-weight: 700;
        }
        .dark .wifi-faq-card p {
          color: #ffffffff !important;
        }
        .dark .wifi-cta { background: #101c33; border-top: 1.5px solid #2c3e61; }
        .dark .wifi-cta h2 { color: #1b4fbdff; }
        .dark .wifi-cta p { color: #ffffffff; }
        .dark .wifi-cta-button { background:#1b4fbdff; }
        .dark .wifi-cta-button:hover { background: #1a6fb7; }
        .dark .wifi-limit-card {
          background: #000000ff;
          color: #fff;
          border: 1.5px solid #fff;
        }
        .dark .wifi-limit-icon {
          color: #f87171;
        }
        .dark .wifi-limit-card h3 {
          color: #1b4fbdff
        }
        .dark .wifi-limit-card p {
          color: #ffffffff;
        }

        main.wifi-page { max-width: 100vw; overflow-x: hidden; font-family: 'Inter', 'Poppins', Arial, sans-serif;}
        .wifi-header { text-align: center; padding: 4rem 2rem 2rem; background: #fff; }
        .wifi-logo { width: 160px; height: auto; margin-bottom: 1rem; transition: filter 0.2s; }
        .wifi-title { font-size: 2.2rem; font-weight: 700; margin-bottom: 0.5rem; color: #1b4fbdff}
        .wifi-subtitle { font-size: 1rem;  max-width: 600px; margin: 0 auto; }

        .wifi-features {display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 2.5rem; padding: 5rem 2rem; max-width: 1100px; margin: 0 auto; }
        .wifi-feature-card { background: #fff; border-radius: 16px; padding: 1.5rem; box-shadow: 0 12px 24px rgba(0,0,0,0.05); text-align: center; border: 1.5px solid #e3eefd; transition: transform .17s, box-shadow .17s; }
        .wifi-feature-card:hover { transform: translateY(-6px) scale(1.02); box-shadow: 0 14px 28px #2297e61b;}
        .wifi-feature-card .wifi-icon { font-size: 2.8rem; color: #1a4bb7; margin-bottom: 0.8rem; }
        .wifi-feature-card h3 { font-size: 1.3rem; font-weight: 700; margin-bottom: 0.5rem; color: #1a4bb7;}
        .wifi-feature-card p { font-size: 0.95rem; line-height: 1.6; }

        .wifi-faq { max-width: 800px; margin: 0 auto 5rem; padding: 0 1rem;}
        .wifi-faq-title { text-align: center; font-size: 2rem; font-weight: 700; margin-bottom: 2rem; color: #1a4bb7;}
        .wifi-faq-card details { background: #fff; border-radius: 12px; padding: 1rem 1.5rem; margin-bottom: 1rem; box-shadow: 0 8px 20px rgba(0,0,0,0.05); cursor: pointer; border: 1.3px solid #e3eefd;}
        .wifi-faq-card details[open] { background: #f8fbff;}
        .wifi-faq-card summary { font-size: 1.1rem; font-weight: 600; list-style: none; cursor: pointer;}
        .wifi-faq-card p { font-size: 0.95rem;  margin-top: 0.6rem; line-height: 1.6;}

        .wifi-limitations {
          padding: 4rem 2rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        .wifi-limitations-title {
          text-align: center;
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: #1b4fbdff;
        }
        .wifi-limitations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 2rem;
        }
        .wifi-limit-card {
          background: #fff;
          border: 1.5px solid #e3eefd;
          padding: 1.5rem;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.04);
          transition: transform 0.2s;
        }
        .wifi-limit-card:hover {
          transform: translateY(-5px);
        }
        .wifi-limit-icon {
          font-size: 2.5rem;
          color: #e63946;
          margin-bottom: 1rem;
        }
        .wifi-limit-card h3 {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.6rem;
          color: #1a4bb7;
        }
        .wifi-limit-card p {
          font-size: 0.95rem;
          
          line-height: 1.6;
        }

        .wifi-cta { background: #f0f7ff; padding: 4rem 2rem; text-align: center; border-top: 1.5px solid #dbeafe; }
        .wifi-cta h2 { font-size: 2rem; font-weight: 700; color: #1a4bb7; margin-bottom: 1rem; }
        .wifi-cta p { font-size: 1rem; color: #333; max-width: 700px; margin: 0 auto 2rem; line-height: 1.7; }
        .wifi-cta-button { display: inline-block; background: #1a4bb7; color: #fff; padding: 0.8rem 1.6rem; font-weight: 600; border-radius: 8px; text-decoration: none; transition: background 0.3s; }
        .wifi-cta-button:hover { background: #1553b8; }

        .wifi-back-btn {
          position: fixed; bottom: 40px; left: 25px;
          width: 45px; height: 45px;
          border-radius: 50%;
          background-color: #1a4bb7;
          color: #fff;
          border: none;
          font-size: 18px;
          font-weight: bold;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          cursor: pointer;
          z-index: 999;
          transition: opacity 0.3s, transform 0.3s;
        }
          .wifi-difference {
  max-width: 960px;
  margin: 4rem auto;
  padding: 0 1.5rem;
}

.wifi-difference-box {
  border: 1.5px solid #e3eefd;
  background: #f9fbff;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
}

.wifi-difference-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a4bb7;
  margin-bottom: 1.5rem;
}

.wifi-difference-subtitle {
  font-size: 1.3rem;
  font-weight: 700;
  margin-top: 2.5rem;
  color: #1a4bb7;
}

.wifi-difference-text {
  font-size: 1rem;
  line-height: 1.8;
  white-space: pre-line;
  
}

.dark .wifi-difference-box {
  background: #182143;
  border-color: #2a4365;
}

.dark .wifi-difference-title,
.dark .wifi-difference-subtitle {
  color: #1b4fbdff;
}

.dark .wifi-difference-text {
  color: #ffffffff;
}


        @media (max-width: 700px) {
          .wifi-features { grid-template-columns: 1fr; gap: 1.2rem; padding: 2.5rem 0.6rem; }
          .wifi-header { padding: 2rem 1rem 1rem; }
        }
      `}</style>

      <main
        className="wifi-page"
        style={{
          direction: language === "ar" ? "rtl" : "ltr",
          fontFamily:
            language === "ar"
              ? "'Cairo', 'Tajawal', Tahoma, Arial, sans-serif"
              : "'Inter', 'Poppins', Arial, sans-serif",
        }}
      >
        <section className="wifi-header" data-aos="zoom-in">
          <img src={WifiLogo} alt="Wi-Fi Logo" className="wifi-logo" />
          <h1 className="wifi-title">{titles[language].main}</h1>
          <p className="wifi-subtitle">{titles[language].subtitle}</p>
        </section>

        <section className="wifi-limitations" data-aos="fade-up">
          <h2 className="wifi-limitations-title">
            {language === "ar"
              ? "لماذا لا نعتمد على الواي فاي في أتمتة منازلنا ؟"
              : "Why We Don’t Rely on Wi-Fi-Based Automation ?"}
          </h2>
          <div className="wifi-limitations-grid">
            {limitations.map((item, i) => (
              <div
                key={i}
                className="wifi-limit-card"
                data-aos="fade-up"
                data-aos-delay={i * 150}
              >
                <div className="wifi-limit-icon">{item.icon}</div>
                <h3>{language === "ar" ? item.ar.title : item.en.title}</h3>
                <p>{language === "ar" ? item.ar.desc : item.en.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="wifi-difference" data-aos="fade-up">
          <div className="wifi-difference-box">
            <h2 className="wifi-difference-title">
              {language === "ar"
                ? "لماذا نختلف عن الآخرين ؟"
                : "Why We’re Different ?"}
            </h2>
            <p className="wifi-difference-text">
              {language === "ar"
                ? "بينما لا تزال بعض الشركات تعتمد على أجهزة واي فاي، نحن في ماس سمارت سوليوشنز لا نفعل ذلك.\n\nنقوم ببناء المنازل الذكية باستخدام بروتوكولات مثل Zigbee و Matter التي تم تطويرها خصيصًا لتوفير أداء مستقر، وتحكم محلي، وأمان عالي"
                : "While many providers still base their systems on Wi-Fi products, we don’t.\n\nAt Mass Smart Solutions, we engineer smart homes using protocols like Zigbee and Matter. These technologies are built for long-term performance, security, and seamless integration."}
            </p>

            <br></br>
            <p className="wifi-difference-text">
              {language === "ar"
                ? "الواي فاي قد يكون مناسبًا لقابس أو لمبة ذكية واحدة، لكنه غير كافٍ لأتمتة منزل متكامل"
                : "Wi-Fi may be fine for a few plugs or bulbs. But when it comes to full-home automation, it becomes a weak link."}
            </p>
          </div>
        </section>

        <section className="wifi-faq" data-aos="fade-up">
          <h2 className="wifi-faq-title">{titles[language].faq}</h2>
          <div className="wifi-faq-card">
            {wifiFAQ[language].map((item, i) => (
              <details key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="wifi-cta" data-aos="fade-up">
          <h2>
            {language === "ar"
              ? "لا نرضى بالحلول المؤقتة"
              : "Don't settle for shortcuts."}
          </h2>
          <p>
            {language === "ar"
              ? " اختر نظامًا ينمو مع احتياجاتك.\n واختر فريقًا يبنيه بالشكل الصحيح"
              : " Choose a system that grows with you.\n Choose a team that builds it right."}
          </p>
          <a
            href="/contact"
            className="wifi-cta-button"
            style={{ marginTop: "1.5rem", display: "inline-block" }}
          >
            {language === "ar"
              ? "👈 احجز استشارة مجانية واكتشف الفرق مع ماس"
              : "👉 Book a free consultation today and discover the Mass difference"}
          </a>
        </section>

        {showButton && (
          <button onClick={scrollToTop} className="wifi-back-btn">
            <FaChevronUp />
          </button>
        )}
      </main>
    </>
  );
};

export default WifiPage;
